'use client';

import {fetchUploadScreenshot, getCompressedImage, StepPayload} from '@blesk-frontend/api';
import {createId} from '@paralleldrive/cuid2';
import {motion} from 'motion/react';
import {useCallback, useState} from 'react';
import {useKey} from 'react-use';

import type {ButtonProps} from '../../../components';
import {Button, Topbar} from '../../../components';
import {getMotionProps} from '../../../libs';
import {blobToFile, scaleChanges} from '../../libs';
import {useImageEditorContext} from '../../providers';
import {useRectStore} from '../../stores';

interface TopBarControlsProps {
  onSaveChanges: (step: StepPayload) => void;
  onClose: () => void;
}

const controlsMotionProps = getMotionProps({
  initial: {y: 4, opacity: 0},
  animate: {y: 0, opacity: 1},
  exit: {y: 0, opacity: 0},
});

const buttonMotionProps = getMotionProps({
  initial: {y: 4, opacity: 0, transition: {delay: 0.16}},
  animate: {y: 0, opacity: 1},
  exit: {y: 0, opacity: 0},
});

interface MotionButtonProps extends ButtonProps {
  layoutId?: string;
}

const MotionButton = ({children, layoutId, variant = 'outline', ...rest}: MotionButtonProps) => (
  <motion.div {...buttonMotionProps} layoutId={layoutId} layout={true}>
    <Button variant={variant} {...rest}>
      {children}
    </Button>
  </motion.div>
);

export const TopBarControls = ({onSaveChanges, onClose}: TopBarControlsProps) => {
  const {step} = useImageEditorContext(({step}) => ({
    step,
  }));
  const [saving, setSaving] = useState(false);

  const {rects, addRect, removeRect} = useRectStore();

  useKey('Delete', () => removeRect());
  useKey('Backspace', () => removeRect());

  const onClickSave = useCallback(async () => {
    setSaving(true);
    if (!step?.src.original) {
      // TODO: show error
      setSaving(false);
      return;
    }

    const {blob, scaledRects} = await scaleChanges({step, rects});
    if (!blob || !scaledRects) {
      // TODO: show error
      setSaving(false);
      return;
    }

    const file = blobToFile(blob, 'edited-screenshot.webp');
    const compressedResult = await getCompressedImage(file);
    const uploadedSrc = await fetchUploadScreenshot(compressedResult.blob, {
      rects: [],
      filename: [`${createId()}.webp`, `${createId()}.webp`],
      prefix: 'editor',
    });

    await fetch(uploadedSrc.original);

    onSaveChanges({
      ...step,
      src: {...step.src, edited: uploadedSrc.original},
      editor: {
        rects: scaledRects,
        crop: step.editor.crop || null,
        zoom: undefined,
        pan: undefined,
        viewport: undefined,
      },
    });

    setSaving(false);
    onClose();
  }, [step, rects, onSaveChanges, onClose]);

  return (
    <Topbar variant="sticky">
      <motion.div
        className="grid h-10 w-full grid-cols-[1fr_4fr_1fr] px-10"
        {...controlsMotionProps}
      >
        <div />
        <div className="flex justify-center gap-4">
          <MotionButton onClick={() => addRect('highlight')}>
            <span>Обвести</span>
          </MotionButton>
          <MotionButton onClick={() => addRect('blur')}>
            <span>Размыть</span>
          </MotionButton>
        </div>
        <div className="flex justify-end gap-4">
          <MotionButton onClick={onClose}>Отменить</MotionButton>
          <MotionButton variant="primary" loading={saving} onClick={onClickSave}>
            Сохранить
          </MotionButton>
        </div>
      </motion.div>
    </Topbar>
  );
};
